import { useRouter } from 'next/router';
import qs from 'query-string';
import { useCallback, useMemo } from 'react';

import { CheckoutStep } from '../types';
import {
  checkoutSteps,
  DEFAULT_CHECKOUT_STEP,
  isCheckoutStep,
} from '../utils/steps';

const STEP_PARAM = 'step';

export type CheckoutStepController = {
  goBack: () => void;
  goNext: () => void;
  goToStep: (step: CheckoutStep) => void;
  step: CheckoutStep;
  steps: readonly CheckoutStep[];
};

const parseAsPath = (asPath: string) => {
  const [pathWithQuery] = asPath.split('#');
  const [pathname] = pathWithQuery.split('?');
  return { pathname, params: qs.parse(qs.extract(pathWithQuery)) };
};

const stepFromAsPath = (
  asPath: string,
  steps: readonly CheckoutStep[],
): CheckoutStep => {
  const value = parseAsPath(asPath).params[STEP_PARAM];
  return isCheckoutStep(value, steps) ? value : DEFAULT_CHECKOUT_STEP;
};

export const useCheckoutStep = (isAuction = false): CheckoutStepController => {
  const router = useRouter();
  const steps = checkoutSteps(isAuction);
  const step = stepFromAsPath(router.asPath, steps);

  const goToStep = useCallback(
    (nextStep: CheckoutStep) => {
      const { params, pathname } = parseAsPath(router.asPath);
      if (params[STEP_PARAM] === nextStep) return;

      void router.push(
        { pathname, query: { ...params, [STEP_PARAM]: nextStep } },
        undefined,
        { shallow: true },
      );
    },
    [router],
  );

  const goNext = useCallback(() => {
    const nextIndex = steps.indexOf(stepFromAsPath(router.asPath, steps)) + 1;
    if (nextIndex >= steps.length) return;

    goToStep(steps[nextIndex]);
  }, [goToStep, router, steps]);

  const goBack = useCallback(() => {
    const previousIndex =
      steps.indexOf(stepFromAsPath(router.asPath, steps)) - 1;
    if (previousIndex < 0) return;

    goToStep(steps[previousIndex]);
  }, [goToStep, router, steps]);

  return useMemo(
    () => ({ goBack, goNext, goToStep, step, steps }),
    [goBack, goNext, goToStep, step, steps],
  );
};
